/**
 * AiEnrichPage — Phase 4g.S (Feb 2026)
 *
 * Direkter Schnellzugriff auf die KI-Artikel-Anreicherung — OHNE Umweg über
 * Einstellungen + Re-Auth. Erreichbar via Button auf der WMS-Seite.
 *
 * Tabs:
 *   1) KI-Batch-Anreicherung (bis 200 Artikel auf einmal)
 *   2) KI-Vorschläge zur Prüfung (Inbox pending Proposals)
 *
 * Route: /dealer/ai-enrich
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Wand2, Sparkles, Settings, Barcode, Euro } from "lucide-react";

import { useAuth } from "../lib/auth";
import BatchAiEnrichPanel from "../components/dealer/BatchAiEnrichPanel";
import ProposalReviewerPanel from "../components/dealer/ProposalReviewerPanel";
import WmsSettingsModal from "../components/dealer/WmsSettingsModal";
import { ProvisionalEanPanel } from "../components/wms/ProvisionalEanPanel";
import UvpResearchPanel from "../components/dealer/UvpResearchPanel";

const TABS = [
    { k: "batch", label: "KI-Batch-Anreicherung", icon: Wand2 },
    { k: "proposals", label: "KI-Vorschläge zur Prüfung", icon: Sparkles },
    { k: "ean", label: "EAN-Nachpflege", icon: Barcode },
    { k: "uvp", label: "Zubehör-UVP & Katalog", icon: Euro },
];

export default function AiEnrichPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("batch");
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        if (user === false) navigate("/login");
        // Jun-2026 (User-Wunsch scholz): Filial-/Verkaufsleiter dürfen ebenfalls rein.
        const isBranchLead = user && user.role === "zweiradverkaeufer"
            && ["filialleiter", "verkaufsleiter"].includes(user.employee_function);
        if (user && user.role !== "b2b" && user.role !== "admin" && !isBranchLead) navigate("/");
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-gray-50" data-testid="ai-enrich-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/wms" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-gray-300 hover:text-white" data-testid="ai-enrich-back">
                        <ArrowLeft className="w-4 h-4" /> Zur Warenwirtschaft
                    </Link>
                    <div className="mt-4">
                        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3">
                            <Wand2 className="w-8 h-8 text-[#FFCC00]" /> KI-Artikel-Anreicherung
                            {/* Jun-2026 (User-Wunsch): Zahnrad rechts vom Titel — öffnet die
                                vorhandenen Einstellungen (Lieferschein-Regeln + KI-Anreicherung). */}
                            <button type="button" onClick={() => setSettingsOpen(true)}
                                className="p-2.5 rounded-sm border border-gray-600 text-gray-300 hover:text-white hover:border-white transition-colors"
                                title="Einstellungen: Lieferschein-Regeln & KI-Artikeldaten-Anreicherung"
                                data-testid="ai-enrich-settings-gear">
                                <Settings className="w-5 h-5" />
                            </button>
                        </h1>
                        <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                            Lassen Sie Artikel-Daten und -Bilder automatisch von der KI vervollständigen —
                            einzeln, im Stapel (bis 200 Artikel) oder als zu prüfende Vorschläge.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
                <div className="flex gap-0 border-b border-gray-200" data-testid="ai-enrich-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.k}
                                type="button"
                                onClick={() => setTab(t.k)}
                                className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${tab === t.k ? "border-[#111827] text-[#111827]" : "border-transparent text-gray-500 hover:text-gray-800"}`}
                                data-testid={`ai-enrich-tab-${t.k}`}
                            >
                                <Icon className="w-4 h-4" /> {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                {tab === "batch" && <BatchAiEnrichPanel />}
                {tab === "proposals" && <ProposalReviewerPanel />}
                {tab === "ean" && <ProvisionalEanPanel standalone />}
                {tab === "uvp" && <UvpResearchPanel />}
            </div>
            <WmsSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </div>
    );
}
